import React, { useState, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Upload, X, FileText, Image, File as FileIcon } from 'lucide-react';
import { toast } from 'sonner';

const getFileIcon = (name) => {
  const ext = name.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return <Image className="h-5 w-5 text-blue-500" />;
  if (['pdf', 'doc', 'docx', 'txt', 'md'].includes(ext)) return <FileText className="h-5 w-5 text-red-500" />;
  return <FileIcon className="h-5 w-5 text-gray-400" />;
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const FileUpload = ({ files = [], onChange, maxFiles = 10, maxSize = 10 * 1024 * 1024, accept }) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList);
    const valid = [];
    for (const file of newFiles) {
      if (file.size > maxSize) { toast.error(`${file.name} exceeds ${formatSize(maxSize)} limit`); continue; }
      if (files.length + valid.length >= maxFiles) { toast.error(`Maximum ${maxFiles} files allowed`); break; }
      valid.push({ id: Date.now() + Math.random(), name: file.name, size: file.size, type: file.type, file, uploadedAt: new Date().toISOString() });
    }
    if (valid.length) {
      onChange([...files, ...valid]);
      toast.success(`${valid.length} file(s) added (demo)`);
    }
  };

  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDragIn = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const handleDragOut = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files); };
  const removeFile = (id) => onChange(files.filter(f => f.id !== id));

  return (
    <div className="space-y-3" data-testid="file-upload">
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${dragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="file-dropzone"
      >
        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Drag & drop files here, or <span className="text-primary font-medium">browse</span></p>
        <p className="text-xs text-muted-foreground/60 mt-1">Max {formatSize(maxSize)} per file, up to {maxFiles} files</p>
        <input ref={inputRef} type="file" multiple accept={accept} className="hidden" onChange={e => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = ''; }} data-testid="file-input" />
      </div>
      {files.length > 0 && (
        <div className="space-y-2" data-testid="file-list">
          {files.map(f => (
            <div key={f.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/20 group">
              {getFileIcon(f.name)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{f.name}</p>
                <p className="text-xs text-muted-foreground">{formatSize(f.size)}</p>
              </div>
              <Badge variant="outline" className="text-[10px]">Uploaded</Badge>
              <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); removeFile(f.id); }} data-testid={`remove-file-${f.id}`}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
